import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import { SectionHeader } from '../components/site/SectionHeader';
import { SEOHead } from '../components/site/SEOHead';
import { JsonLd } from '../components/site/JsonLd';
import api, { getFileUrl } from '../lib/api';

export default function Blog() {
  const { slug } = useParams();
  return slug ? <BlogDetail slug={slug} /> : <BlogList />;
}

function BlogList() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/blog/posts?published_only=true'), api.get('/blog/categories')])
      .then(([p, c]) => { setPosts(p.data); setCategories(c.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filterCat ? posts.filter(p => p.category_id === filterCat) : posts;

  return (
    <div data-testid="blog-page">
      <SEOHead title="Haberler" />
      <div className="relative py-16 md:py-24 bg-[#0f1419]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">Haberler</h1>
          <p className="text-gray-300 mt-2">Haberler ve güncel yazılar</p>
          <div className="mt-4 w-16 h-1 rounded-full bg-[hsl(var(--gold))]" />
        </div>
      </div>

      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {categories.length > 0 && (
            <div className="flex gap-2 mb-8 flex-wrap">
              <button onClick={() => setFilterCat('')} className={`px-4 py-2 text-sm rounded-lg ${!filterCat ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'bg-secondary'}`}>Tümü</button>
              {categories.map(c => <button key={c.id} onClick={() => setFilterCat(c.id)} className={`px-4 py-2 text-sm rounded-lg ${filterCat===c.id ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'bg-secondary'}`}>{c.name}</button>)}
            </div>
          )}

          {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
          : filtered.length === 0 ? <div className="text-center py-20 text-muted-foreground">Henüz yazı yok</div>
          : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(post => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)] overflow-hidden hover:border-[hsl(var(--gold)/0.45)] transition-colors">
                <div className="aspect-[16/9] overflow-hidden bg-secondary">
                  {post.cover_image ? <img src={getFileUrl(post.cover_image)} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground">Görsel yok</div>}
                </div>
                <div className="p-5">
                  {post.category_name && <span className="text-xs font-medium text-[hsl(var(--gold))]">{post.category_name}</span>}
                  <h3 className="font-heading font-semibold text-lg mt-1 mb-2 line-clamp-2">{post.title}</h3>
                  {post.excerpt && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.excerpt}</p>}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {post.author && <span className="flex items-center gap-1"><User className="w-3 h-3" />{post.author}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>}
        </div>
      </section>
    </div>
  );
}

function BlogDetail({ slug }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get(`/blog/posts/${slug}`).then(r => setPost(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;
  if (!post) return <div className="min-h-screen flex flex-col items-center justify-center"><p className="text-muted-foreground mb-4">Yazı bulunamadı</p><Link to="/blog" className="text-[hsl(var(--gold))]">Haberlere dön</Link></div>;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: post.title,
    ...(post.excerpt ? { description: post.excerpt } : {}),
    ...(post.cover_image ? { image: [getFileUrl(post.cover_image)] } : {}),
    ...(post.author ? { author: { '@type': 'Person', name: post.author } } : {}),
    ...(post.created_at ? { datePublished: new Date(post.created_at).toISOString() } : {}),
    mainEntityOfPage: `${origin}/blog/${post.slug}`,
  };

  return (
    <div data-testid="blog-detail-page">
      <SEOHead title={post.title} description={post.excerpt} />
      <JsonLd id="blog-article" data={articleLd} />
      {post.cover_image && (
        <div className="relative h-[40vh] md:h-[50vh] bg-[#0f1419]">
          <img src={getFileUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-[#0f1419]/30 to-transparent" />
        </div>
      )}
      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> Haberlere Dön</Link>
          {post.category_name && <span className="block text-sm font-medium text-[hsl(var(--gold))] mb-2">{post.category_name}</span>}
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
            {post.author && <span className="flex items-center gap-1"><User className="w-4 h-4" />{post.author}</span>}
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(post.created_at).toLocaleDateString('tr-TR')}</span>
            {post.tags && <span className="flex items-center gap-1"><Tag className="w-4 h-4" />{post.tags}</span>}
          </div>
          <div className="prose max-w-none text-sm md:text-base leading-relaxed text-muted-foreground whitespace-pre-line">{post.content}</div>
        </div>
      </section>
    </div>
  );
}
